import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { Check, ChevronDown, ChevronRight, Image as ImageIcon, Layers, Sliders } from 'lucide-react';

import { cn } from '../../lib/cn';
import { colorForClassIndex } from '../../utils/classColors';
import BoxesPanel from './BoxesPanel';
import BoxPropertiesPanel from './BoxPropertiesPanel';
import ClassesPanel from './ClassesPanel';
import type { BoundingBox, Class as ClassModel, ImageMeta } from '../../types/api';

/**
 * RightRail — controlled view picker (Classes / My Canvas / Box Properties).
 *
 * View state is OWNED BY THE PARENT (Annotator) so different selection
 * paths can each decide whether to auto-switch to Box Properties:
 *   • Canvas click on a box        → parent switches to 'properties'
 *   • List row click in My Canvas  → no switch (browse stays where it is)
 *   • Row 'Open in Properties' icon → parent switches
 *   • Context-menu 'Open in Box Properties' → parent switches
 *   • Right-click on anything      → no switch
 */
export type RightRailView = 'classes' | 'boxes' | 'properties';

interface RightRailProps {
    isDarkMode?: boolean;
    classes: ClassModel[];
    boxes: BoundingBox[];
    activeClassIndex: number;
    selectedBox: BoundingBox | null;
    activeImage: ImageMeta | null;
    orgId?: string;
    naturalSize?: { w: number; h: number } | null;
    hiddenClassIndices: Set<number>;
    drawPriorityMode: boolean;
    /** True when the parent project is locked — hides the rename
     *  pencil + any other mutation affordances in the right rail. */
    isLocked?: boolean;
    /** Optional — when provided AND project unlocked, the "My Canvas"
     *  header row's pencil becomes a separate button that opens the
     *  RenameImageModal. */
    onRenameImage?: (image: ImageMeta) => void;
    view: RightRailView;
    onViewChange: (v: RightRailView) => void;
    onSelectActive: (classIndex: number) => void;
    onToggleClassVisibility: (classIndex: number) => void;
    onAddClassClick: () => void;
    onDeleteClassClick: (cls: ClassModel, inUseCount: number) => void;
    onEditClassClick: (cls: ClassModel) => void;
    /** Highlight box on canvas; do not switch view. */
    onSelectBoxOnly: (id: string) => void;
    /** Highlight + switch to Box Properties view. */
    onOpenBoxInProperties: (id: string) => void;
    onChangeBox: (id: string, patch: Partial<BoundingBox>) => void;
    onDeleteBox: (id: string) => void;
    onToggleDrawPriority: () => void;
    onBringBoxForward: (id: string) => void;
    onSendBoxBackward: (id: string) => void;
    onContextMenuBox: (id: string, x: number, y: number) => void;
    footer?: ReactNode;
    /** When provided, a close-chevron renders in the top header row (only
     *  visible <lg). Lets the drawer be collapsed without using the
     *  backdrop tap. */
    onCollapse?: () => void;
}

export default function RightRail({
    isDarkMode = false,
    classes,
    boxes,
    activeClassIndex,
    selectedBox,
    activeImage,
    orgId,
    naturalSize,
    hiddenClassIndices,
    drawPriorityMode,
    isLocked = false,
    onRenameImage,
    view,
    onViewChange,
    onSelectActive,
    onToggleClassVisibility,
    onAddClassClick,
    onDeleteClassClick,
    onEditClassClick,
    onSelectBoxOnly,
    onOpenBoxInProperties,
    onChangeBox,
    onDeleteBox,
    onToggleDrawPriority,
    onBringBoxForward,
    onSendBoxBackward,
    onContextMenuBox,
    footer,
    onCollapse,
}: RightRailProps) {
    const { t } = useTranslation('canvas');

    const activeClass = classes.find((c) => c.class_index === activeClassIndex) ?? null;
    const activeColor = activeClass?.color || colorForClassIndex(activeClassIndex);
    const visibleBoxCount = boxes.filter((b) => !hiddenClassIndices.has(b.class_index)).length;

    const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

    // Per-view icon tints — Agent Builder pattern: each view's icon reads
    // as a unique chip color so the dropdown row is scannable at a glance.
    //   classes    → emerald (taxonomy / structure)
    //   my canvas  → primary blue (the work-in-progress surface)
    //   properties → amber (per-box inspect / tweak)
    const views: Array<{ key: RightRailView; label: string; icon: React.ComponentType<{ className?: string }>; iconClassName: string; count?: number }> = [
        {
            key: 'classes',
            label: t('view_classes', { defaultValue: 'Classes' }),
            icon: Layers,
            iconClassName: isDarkMode ? 'text-emerald-400' : 'text-emerald-600',
            count: classes.length,
        },
        {
            key: 'boxes',
            label: t('view_my_canvas', { defaultValue: 'My Canvas' }),
            icon: ImageIcon,
            iconClassName: isDarkMode ? 'text-primary-400' : 'text-primary-600',
            count: visibleBoxCount,
        },
        {
            key: 'properties',
            label: t('view_box_properties', { defaultValue: 'Properties' }),
            icon: Sliders,
            iconClassName: isDarkMode ? 'text-amber-400' : 'text-amber-600',
        },
    ];
    const current = views.find((v) => v.key === view) ?? views[0];

    return (
        <aside
            className={cn(
                // Fill the parent: full width + full height of whatever
                // container the host put around us. On standalone-mode lg+
                // the parent IS the body flex, so we fall back to a fixed
                // w-80 if no other width is imposed.
                'w-full h-full border-l flex flex-col overflow-hidden',
                isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white',
            )}
        >
            {/* Persistent header — active class always visible */}
            <div
                className={cn(
                    'px-4 py-3 border-b flex items-center gap-2',
                    isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50',
                )}
            >
                <span className={cn('text-[11px] font-bold uppercase tracking-wider', muted)}>
                    {t('class_label', { defaultValue: 'Class' })}
                </span>
                {activeClass ? (
                    // Active class chip doubles as a shortcut — click jumps
                    // straight to the Classes panel for renaming / recoloring
                    // / deleting without hunting through the View dropdown.
                    <button
                        type="button"
                        onClick={() => onViewChange('classes')}
                        title={t('jump_to_classes', { defaultValue: 'Open Classes panel' })}
                        className={cn(
                            'inline-flex items-center gap-2 min-w-0 px-1.5 py-0.5 -mx-1.5 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                            isDarkMode ? 'hover:bg-gray-700/60' : 'hover:bg-gray-200/70',
                        )}
                    >
                        <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: activeColor }} />
                        <span className={cn('text-sm font-semibold truncate', isDarkMode ? 'text-gray-100' : 'text-gray-900')}>
                            {activeClass.name}
                        </span>
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => onViewChange('classes')}
                        className={cn('text-sm italic underline-offset-2 hover:underline', muted)}
                    >
                        {t('no_class_yet', { defaultValue: 'No class — add one to start' })}
                    </button>
                )}
                {/* Spacer — pushes the close chevron to the right edge.
                    Only rendered when an onCollapse handler was provided,
                    and the chevron itself hides on lg+ (docked layout). */}
                {onCollapse && (
                    <>
                        <span className="flex-1" />
                        <button
                            type="button"
                            onClick={onCollapse}
                            aria-label={t('close_right_rail', { defaultValue: 'Close panel' })}
                            title={t('close_right_rail', { defaultValue: 'Close panel' })}
                            className={cn(
                                'p-1.5 rounded-md transition-colors shrink-0',
                                isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500',
                            )}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </>
                )}
            </div>

            {/* View picker */}
            <div className={cn('px-3 py-2 border-b', isDarkMode ? 'border-gray-700' : 'border-gray-200')}>
                <Listbox value={view} onChange={onViewChange}>
                    <div className="relative">
                        <Listbox.Button
                            className={cn(
                                'w-full inline-flex items-center gap-2 px-3 py-2 rounded-md border text-[13px] font-semibold transition-colors',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                                isDarkMode
                                    ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700'
                                    : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50',
                            )}
                        >
                            <current.icon className={cn('h-3.5 w-3.5 shrink-0', current.iconClassName)} />
                            <span className="flex-1 text-left truncate">{current.label}</span>
                            {current.count !== undefined && (
                                <span
                                    className={cn(
                                        'text-[10px] tabular-nums px-1.5 rounded-full',
                                        isDarkMode ? 'bg-primary-500/20 text-primary-300' : 'bg-primary-50 text-primary-700',
                                    )}
                                >
                                    {current.count}
                                </span>
                            )}
                            <ChevronDown className={cn('h-3.5 w-3.5 shrink-0', muted)} />
                        </Listbox.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Listbox.Options
                                className={cn(
                                    'absolute left-0 right-0 mt-1 z-50 rounded-md border shadow-xl overflow-hidden py-1 text-[13px]',
                                    isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200',
                                )}
                            >
                                {views.map((v) => (
                                    <Listbox.Option
                                        key={v.key}
                                        value={v.key}
                                        className={({ active, selected }) =>
                                            cn(
                                                'px-3 py-2 flex items-center gap-2 cursor-pointer',
                                                active ? (isDarkMode ? 'bg-gray-800' : 'bg-gray-50') : (isDarkMode ? 'text-gray-300' : 'text-gray-700'),
                                                selected && (isDarkMode ? 'text-primary-300' : 'text-primary-700'),
                                            )
                                        }
                                    >
                                        {({ selected }) => (
                                            <>
                                                <v.icon className={cn('h-3.5 w-3.5 shrink-0', v.iconClassName)} />
                                                <span className="flex-1 truncate">{v.label}</span>
                                                {v.count !== undefined && (
                                                    <span className={cn('text-[10px] tabular-nums', muted)}>{v.count}</span>
                                                )}
                                                {selected && <Check className={cn('h-3.5 w-3.5', isDarkMode ? 'text-primary-300' : 'text-primary-600')} />}
                                            </>
                                        )}
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </Transition>
                    </div>
                </Listbox>
            </div>

            {/* Active view content */}
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                {view === 'classes' && (
                    <ClassesPanel
                        isDarkMode={isDarkMode}
                        classes={classes}
                        boxes={boxes}
                        activeClassIndex={activeClassIndex}
                        hiddenClassIndices={hiddenClassIndices}
                        onSelectActive={onSelectActive}
                        onToggleVisibility={onToggleClassVisibility}
                        onAddClick={onAddClassClick}
                        onDeleteClass={onDeleteClassClick}
                        onEditClick={onEditClassClick}
                    />
                )}
                {view === 'boxes' && (
                    <BoxesPanel
                        isDarkMode={isDarkMode}
                        boxes={boxes}
                        classes={classes}
                        selectedBoxId={selectedBox?.id ?? null}
                        hiddenClassIndices={hiddenClassIndices}
                        naturalSize={naturalSize}
                        activeImage={activeImage}
                        orgId={orgId}
                        drawPriorityMode={drawPriorityMode}
                        isLocked={isLocked}
                        onRenameImage={onRenameImage}
                        onSelectBoxOnly={onSelectBoxOnly}
                        onOpenBoxInProperties={onOpenBoxInProperties}
                        onDeleteBox={onDeleteBox}
                        onToggleDrawPriority={onToggleDrawPriority}
                        onBringBoxForward={onBringBoxForward}
                        onSendBoxBackward={onSendBoxBackward}
                        onContextMenuBox={onContextMenuBox}
                    />
                )}
                {view === 'properties' && (
                    <BoxPropertiesPanel
                        isDarkMode={isDarkMode}
                        selectedBox={selectedBox}
                        classes={classes}
                        naturalSize={naturalSize}
                        onChange={onChangeBox}
                        onDelete={onDeleteBox}
                    />
                )}
            </div>
            {footer}
        </aside>
    );
}
